import React, { useState, useEffect, useMemo, useCallback } from "react";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AppHeader from "@/components/drill/AppHeader";
import PullToRefresh from "@/components/PullToRefresh";
import { Calendar, ChevronRight, FileText, Search, RotateCcw, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/drill/StatusBadge";
import DashboardFilterDialog from "@/components/drill/DashboardFilterDialog";
import RecentlyDeletedHoles from "@/components/drill/RecentlyDeletedHoles";
import { useAuth } from "@/lib/AuthContext";
import { isAtLeast } from "@/lib/roles";
import { getTotalDepth } from "@/lib/depthCalculations";

import { safeLocalStorage } from '@/lib/browserStorage';
export default function Dashboard() {
  const { effectiveRole, user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [runs, setRuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    rigs: new Set(),
    methods: new Set(),
    drillers: new Set(),
  });

  const refresh = useCallback(async () => {
    try {
      const [s, r, air, rc, u] = await Promise.all([
        base44.entities.Sheet.list("-created_date"),
        base44.entities.DrillRun.list(),
        base44.entities.AirCoreRun.list(),
        base44.entities.RCRun.list(),
        effectiveRole === "rig" ? Promise.resolve([]) : base44.entities.User.list(),
      ]);
      const methodRuns = [...(air || []), ...(rc || [])].map((x) => ({ ...x, record_type: x.record_type || 'run' }));
      setSheets(s);
      setRuns([...(r || []), ...methodRuns]);
      setUsers(u || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [effectiveRole]);

  const handleReopen = async (id) => {
    if (!isAtLeast(effectiveRole, "supervisor")) return;
    try {
      await base44.entities.Sheet.update(id, { status: "Active" });
      refresh();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);



  const filterOptions = useMemo(() => {
    const rigs = new Set();
    const methods = new Set();
    const drillers = new Set();
    sheets.forEach((s) => {
      if (s.rig_id) rigs.add(s.rig_id);
      if (s.hole_type) methods.add(s.hole_type);
      (s.drillers || []).forEach((d) => drillers.add(d));
    });
    return {
      rigs: [...rigs].sort(),
      methods: [...methods].sort(),
      drillers: [...drillers].sort(),
    };
  }, [sheets]);

  const activeFilterCount = useMemo(
    () =>
      selectedFilters.rigs.size + selectedFilters.methods.size + selectedFilters.drillers.size,
    [selectedFilters]
  );

  const rigIdentifiers = useMemo(() => new Set([user?.rig_id, user?.full_name, user?.name, user?.id].filter(Boolean).map(String)), [user]);

  const displayed = useMemo(() => {
    let active = sheets.filter((s) => s.status !== "Completed");
    if (effectiveRole === "rig") {
      active = active.filter((s) => rigIdentifiers.has(String(s.rig_id || "")) || rigIdentifiers.has(String(s.rig_user_id || "")));
    }
    if (selectedFilters.rigs.size > 0) {
      active = active.filter((s) => selectedFilters.rigs.has(s.rig_id));
    }
    if (selectedFilters.methods.size > 0) {
      active = active.filter((s) => selectedFilters.methods.has(s.hole_type));
    }
    if (selectedFilters.drillers.size > 0) {
      active = active.filter((s) =>
        (s.drillers || []).some((d) => selectedFilters.drillers.has(d))
      );
    }
    if (selectedUser) active = active.filter((s) => s.created_by_id === selectedUser || s.rig_user_id === selectedUser);
    if (dateFrom) active = active.filter((s) => !s.created_date || s.created_date.slice(0,10) >= dateFrom);
    if (dateTo) active = active.filter((s) => !s.created_date || s.created_date.slice(0, 10) <= dateTo);
    if (search.trim()) {
      const q = search.toLowerCase();
      active = active.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q)
      );
    }
    return active;
  }, [sheets, search, selectedFilters, effectiveRole, user, selectedUser, dateFrom, dateTo, rigIdentifiers]);

  const completed = useMemo(() => {
    let cs = sheets.filter((s) => s.status === "Completed");
    if (effectiveRole === "rig") {
      cs = cs
        .filter((s) => rigIdentifiers.has(String(s.rig_id || "")) || rigIdentifiers.has(String(s.rig_user_id || "")))
        .sort((a, b) => new Date(b.completed_at || b.created_date || 0) - new Date(a.completed_at || a.created_date || 0))
        .slice(0, 1);
    }
    if (selectedFilters.rigs.size > 0) {
      cs = cs.filter((s) => selectedFilters.rigs.has(s.rig_id));
    }
    if (selectedFilters.methods.size > 0) {
      cs = cs.filter((s) => selectedFilters.methods.has(s.hole_type));
    }
    if (selectedFilters.drillers.size > 0) {
      cs = cs.filter((s) =>
        (s.drillers || []).some((d) => selectedFilters.drillers.has(d))
      );
    }
    if (selectedUser) cs = cs.filter((s) => s.created_by_id === selectedUser || s.rig_user_id === selectedUser);
    if (dateFrom) cs = cs.filter((s) => !s.created_date || s.created_date.slice(0,10) >= dateFrom);
    if (dateTo) cs = cs.filter((s) => !s.created_date || s.created_date.slice(0, 10) <= dateTo);
    if (!search.trim()) return cs;
    const q = search.toLowerCase();
    return cs.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [sheets, search, selectedFilters, effectiveRole, user, selectedUser, dateFrom, dateTo, rigIdentifiers]);



  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Dashboard" subtitle="Browse and search your holes" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24">
        {(safeLocalStorage.getItem("drilllog_company_logo") || user?.company_logo) && (
          <div className="flex justify-center mb-5">
            <img src={safeLocalStorage.getItem("drilllog_company_logo") || user?.company_logo} alt={user?.organisation_name || user?.organization_name || user?.company_name || "Company logo"} className="max-h-12 w-auto max-w-[55%] object-contain" />
          </div>
        )}
        <PullToRefresh onRefresh={refresh}>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Search holes by ID or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-xl bg-white dark:bg-slate-900 dark:border-slate-800"
            />
          </div>

          {effectiveRole !== "rig" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className="h-10 rounded-xl border bg-white px-3 text-sm dark:bg-slate-900 dark:border-slate-800">
                <option value="">All users</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>)}
              </select>
              <div className="space-y-1.5">
                <label htmlFor="dashboard-date-from" className="block text-xs font-semibold text-slate-600 dark:text-slate-300">From date</label>
                <Input id="dashboard-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" className="rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="dashboard-date-to" className="block text-xs font-semibold text-slate-600 dark:text-slate-300">To date</label>
                <Input id="dashboard-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" className="rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="outline"
              onClick={() => setFilterOpen(true)}
              className="rounded-xl gap-2 bg-white dark:bg-slate-900 dark:border-slate-800"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>


          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 dark:text-slate-400">
            {search.trim() ? "Search Results" : "All Holes"}
          </h3>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin dark:border-slate-800 dark:border-t-slate-300" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3 dark:text-slate-700" />
              <p className="text-slate-500 font-medium dark:text-slate-400">
                {search.trim() ? "No matching holes" : "No holes yet"}
              </p>
              <p className="text-slate-400 text-sm mt-1 dark:text-slate-500">
                {search.trim()
                  ? "Try a different search term."
                  : "Create a new hole from the Home page."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayed.map((s) => {
                return (
                  <Link
                    key={s.id}
                    to={(s.hole_type === "Air Core" || s.hole_type === "RC") ? `/method-run-sheet/${s.id}/${s.hole_type === "RC" ? "rc" : "air-core"}` : `/run-sheet/${s.id}`}
                    className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 transition-colors group dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 dark:bg-slate-800 dark:text-slate-300">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-900 truncate dark:text-slate-100">
                          {s.name}
                        </h4>
                        {s.description && (
                          <p className="text-sm text-slate-500 line-clamp-1 dark:text-slate-400">
                            {s.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-slate-400 hidden sm:inline-flex items-center gap-1 dark:text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {s.created_date
                          ? new Date(s.created_date).toLocaleDateString()
                          : "—"}
                      </span>
                      <StatusBadge status={s.status} />
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {(() => {
  const rs = runs.filter(r => r.sheet_id === s.id);
  // Rods in Hole must match the Run Sheet: use the rods-in-hole value from
  // the deepest completed drilling record, rather than summing each run.
  // Split Conventional Core depth is resolved through the same authoritative
  // Total Depth helper used by the Run Sheet.
  const completed = rs.filter(r => r.to_depth != null);
  const deepest = completed.reduce((best, r) => {
    if (!best) return r;
    const d = Number(getTotalDepth(r, s.hole_type, s.run_size) ?? r.to_depth ?? r.from_depth) || 0;
    const bd = Number(getTotalDepth(best, s.hole_type, s.run_size) ?? best.to_depth ?? best.from_depth) || 0;
    return d >= bd ? r : best;
  }, null);
  const fallback = [...rs]
    .filter(r => r.rods_in_hole != null)
    .sort((a,b) => (Number(getTotalDepth(b, s.hole_type, s.run_size) ?? b.to_depth ?? b.from_depth) || 0) - (Number(getTotalDepth(a, s.hole_type, s.run_size) ?? a.to_depth ?? a.from_depth) || 0))[0];
  const rods = Number(deepest?.rods_in_hole ?? fallback?.rods_in_hole);
  // Hole summary depth is the deepest completed physical end, not the sum of
  // individual run intervals. Split Conventional Core runs include Rod 2.
  const metres = rs.reduce((max, r) => Math.max(max, getTotalDepth(r, s.hole_type, s.run_size) ?? 0), 0);
  return `${Number.isFinite(rods) ? rods : 0} rods and ${metres.toFixed(2)} m`;
})()}
                      </span>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-400" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}


          {completed.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 dark:text-slate-400">
                Completed
              </h3>
              <div className="space-y-2">
                {completed.map((s) => (
                  <div
                    key={s.id}
                    className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between gap-3 dark:bg-slate-900 dark:border-slate-800"
                  >
                    <Link
                      to={(s.hole_type === "Air Core" || s.hole_type === "RC") ? `/method-run-sheet/${s.id}/${s.hole_type === "RC" ? "rc" : "air-core"}` : `/run-sheet/${s.id}`}
                      className="flex items-center gap-3 min-w-0 flex-1"
                    >
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 dark:bg-slate-800 dark:text-slate-300">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-slate-900 truncate dark:text-slate-100">
                          {s.name}
                        </h4>
                        {s.description && (
                          <p className="text-sm text-slate-500 line-clamp-1 dark:text-slate-400">
                            {s.description}
                          </p>
                        )}
                      </div>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={s.status} />
                      {isAtLeast(effectiveRole, "supervisor") && <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReopen(s.id)}
                        className="rounded-xl gap-1.5"
                        title="Reopen this hole"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Reopen
                      </Button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <RecentlyDeletedHoles onChanged={refresh} />
        </PullToRefresh>

        <DashboardFilterDialog
          open={filterOpen}
          onOpenChange={setFilterOpen}
          options={filterOptions}
          selected={selectedFilters}
          onApply={setSelectedFilters}
        />
      </main>
    </div>
  );
}